import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Avatar } from '@fiverr/fit';
import { GigCardShell, Media, Title, SellerInfo, SellerLevel, Username, ListViewInfo, SellerCountry, SellerResponseTime, Price, GigBadges, Rating, CollectGig } from '../shared';
import { getCurrency, adaptGigData, getGigAssets, isRisingTalent, gigCardExperiments } from '../utils';
import './style.scss';

class ListViewItem extends PureComponent {

    constructor(props) {
        super(props);

        this.LAZY_LOADING_THRESHOLD = props.context.useProgressiveLoading ? 12 : 48;

        this.state = {
            shouldLoad: props.gigIndex < this.LAZY_LOADING_THRESHOLD
        };

        this.startLazyLoad = this.startLazyLoad.bind(this);
    }

    componentDidMount() {
        document.addEventListener('DOMContentLoaded', this.startLazyLoad, false);
    }

    startLazyLoad() {
        this.setState({
            shouldLoad: true
        });
    }

    render() {
        const {
            gig,
            user,
            updateCollections,
            context,
            onLinkClick
        } = this.props;

        const inExperiment = gigCardExperiments(context.abTests),
            userId = user && user.userId || null,
            risingTalent = isRisingTalent(gig.type),
            showHighCompletionRate = inExperiment('HIGH_COMPLETION_RATE_GIG_BADGE') && gig.high_completion_rate,
            showRecentRatings = inExperiment('LAST_60_DAYS_RATINGS') && (!gig.is_pro && gig.last_60_days_rating_count > 0),
            isLimitedEdition = gig.is_limited_edition,
            shouldRenderRating = (gig.rating > 0 || gig.is_pro) && !risingTalent && !isLimitedEdition,
            isProExperience = inExperiment('PRO_EXPERIENCE') && gig.is_pro,
            tooltipPositionClass = 'hint--bottom-right',
            showSellerLevel = !risingTalent && !isProExperience,
            sellerCountryComma = (!!gig.seller_level || gig.is_pro) && !isProExperience,
            tabTarget = inExperiment('OPEN_NEW_TAB', 3) ? '_blank' : '_self';

        const onLinkClickWrapper = (e, type = 'gig') => {
            onLinkClick(e, {type, gig});
        };

        adaptGigData(gig, {context});

        return (
            <GigCardShell key={gig.gig_id} type="list-view-card" gigId={gig.gig_id}
                isProExperience={isProExperience}
                isLimitedEdition={isLimitedEdition}>
                <div className="flex-row">
                    <div className="col-media">
                        <a href={gig.gig_url} className="js-gig-card-imp-data" onClick={onLinkClickWrapper} target={tabTarget}>
                            <Media assets={getGigAssets(gig)} gigId={gig.gig_id} shouldLoad={this.state.shouldLoad} userId={userId}/>
                        </a>
                    </div>
                    <div className="col-gig-info">
                        <Title gigUrl={gig.gig_url} caption={gig.iwill_title} onLinkClick={onLinkClickWrapper} tabTarget={tabTarget}/>
                        <div className="desktop">
                            <SellerInfo>
                                {gig.is_seller_online && <span className="is-online"></span>}
                                {this.state.shouldLoad && <Avatar src={gig.seller_img}/>}
                                <Username name={gig.seller_name} url={gig.seller_url} prefix={i18n.t('general.by')}
                                    context={context}/>
                                {showSellerLevel &&
                                <SellerLevel level={gig.seller_level} isPro={gig.is_pro}
                                    isNewSeller={gig.is_new_seller}
                                    tooltipPositionClass={tooltipPositionClass}/>}
                                {gig.seller_country_name &&
                                <SellerCountry country={gig.seller_country_name} comma={sellerCountryComma}/>}
                                {gig.seller_avg_response &&
                                <SellerResponseTime responseTime={gig.seller_avg_response} comma={true}/>}
                            </SellerInfo>
                            <Price gigUrl={gig.gig_url}
                                price={getCurrency(gig.price_i, true)}
                                showStartingAt={gig.show_starting_at}
                                isLimitedEdition={isLimitedEdition}
                                averagePrice={getCurrency(gig.average_price)}
                                medianPrice={getCurrency(gig.median_price)}
                                onLinkClick={onLinkClickWrapper}/>
                        </div>
                        <div className="rating-badges-container">
                            {shouldRenderRating &&
                            <Rating score={gig.rating} recentScore={gig.last_60_days_rating_avg} gigId={gig.gig_id}
                                votes={gig.rating_count} recentRatingsCount={gig.last_60_days_rating_count}
                                starSize={16} starCount={1} isPro={gig.is_pro}
                                recentRatingsExp={showRecentRatings}
                                isProExperience={isProExperience}/>}
                            {this.state.shouldLoad && <GigBadges featured={gig.is_featured}
                                limitedEdition={gig.is_limited_edition}
                                limitedEditionDeadline={gig.limited_edition_expires_at}
                                qualityChoice={gig.quality_choice}
                                reliable={showHighCompletionRate}
                                availableNow={gig.is_available_now}
                                type={gig.type}
                                sellerName={gig.seller_name}
                                pro={gig.is_pro}
                                gigId={gig.gig_id}
                                tooltipPositionClass={tooltipPositionClass}
                                isProExperience={isProExperience}/> }
                        </div>
                        <ListViewInfo platforms={gig.metadata && gig.metadata.platform ? gig.metadata.platform : null}
                            skills={gig.skills}
                            avgDeliveryTime={gig.avg_delivery_time}/>
                        {this.state.shouldLoad && <CollectGig gigId={gig.gig_id} sellerId={gig.seller_id}
                            updateCollections={updateCollections}
                            user={user} showCollMenu={true} icon="fa fa-ellipsis-v"/> }
                    </div>
                </div>
                <div className="flex-row space-between mobile">
                    <SellerInfo>
                        {gig.is_seller_online && <span className="is-online"></span>}
                        {this.state.shouldLoad && <Avatar src={gig.seller_img}/>}
                        <div className="seller-info-wrapper">
                            <Username name={gig.seller_name} url={gig.seller_url} prefix={i18n.t('general.by')}
                                context={context}/>
                            {showSellerLevel &&
                            <SellerLevel level={gig.seller_level} isPro={gig.is_pro}
                                isLimitedEdition={isLimitedEdition}
                                isNewSeller={gig.is_new_seller}
                                tooltipPositionClass={tooltipPositionClass}/>}
                        </div>
                    </SellerInfo>

                    <Price gigUrl={gig.gig_url}
                        price={getCurrency(gig.price_i, true)}
                        showStartingAt={gig.show_starting_at}
                        isLimitedEdition={isLimitedEdition}
                        averagePrice={getCurrency(gig.average_price)}
                        medianPrice={getCurrency(gig.median_price)}
                        onLinkClick={onLinkClickWrapper}/>
                </div>
            </GigCardShell>
        );
    }
}


ListViewItem.propTypes = {
    gig: PropTypes.object,
    updateCollections: PropTypes.func,
    context: PropTypes.object,
    onLinkClick: PropTypes.func
};

ListViewItem.defaultProps = {
    context: {},
    gigIndex: 0,
    onLinkClick: () => null
};

export default ListViewItem;



// WEBPACK FOOTER //
// ./src/list_view_card/ListView.js